import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import cn from 'classnames';
import PropTypes from 'prop-types';
import commaNumber from 'comma-number';
import './EarningsPortalCommissions.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import Tooltip from '../General/Tooltip';
import Image from '../General/Image';

import { downloadCommissions as downloadCommissionsAPI } from '../../APIClient/payouts';

import { getUserId } from '../../Helpers/user_helpers';
import { downloadCsvFromUrl } from '../../Helpers/helpers';
import { getPrettyDate } from '../../Helpers/formatting';
import { DEVICE_WIDTH_BREAKPOINTS } from '../../Helpers/constants';

const EarningsPortalCommissions = props => {
  const { user, payoutsAreLoading, type } = props;

  const [payoutsVisible, setPayoutsVisible] = useState(5);
  const [isDownloading, setIsDownloading] = useState(false);

  // Items
  let items = [];
  if (type === 'normal_commissions') items = _.get(user, 'payouts.payout_summaries.normal_commissions', []);
  if (type === 'opportunity_commissions') items = _.get(user, 'payouts.payout_summaries.opportunity_commissions', []);
  if (type === 'all_commissions') items = _.get(user, 'payouts.payout_summaries.payouts', []);

  // Title
  let title = '';
  if (type === 'normal_commissions') title = 'Commissions';
  if (type === 'opportunity_commissions') title = 'Opportunity Payments';
  if (type === 'all_commissions') title = 'All Commissions';

  // Subtitle
  let subtitle = '';
  if (type === 'opportunity_commissions') {
    const { opportunity_commissions } = _.get(user, 'payouts.payout_summaries.total_earnings', {});
    if (items.length > 5 && opportunity_commissions) {
      subtitle = `You've earned $${commaNumber(opportunity_commissions)} through opportunities.`;
    }
  }

  // Empty message
  let emptyMessage = '';
  if (type === 'opportunity_commissions') {
    emptyMessage = 'Payments from brand opportunities will appear here.';
  } else {
    emptyMessage = 'Purchases made through your shop & links will appear here.';
  }

  // Columns
  let columns = [];
  if (type === 'opportunity_commissions') {
    columns = [
      {
        display: 'Date',
        isLeftAligned: true,
        size: 1,
        getDisplay: p => getPrettyDate(p.transaction_date)
      },
      { display: 'Opportunity', isLeftAligned: true, size: 3, mobileSize: 2, getDisplay: p => p.merchant },
      {
        display: 'Bonus',
        size: 1,
        getDisplay: p => (p.amountEarned ? '$' + commaNumber(p.amountEarned.toFixed(0)) : '-')
      },
      {
        display: 'Status',
        size: 1,
        getDisplay: p => (
          <>
            {p.statusDisplay}
            {p.infoTooltip && <Tooltip message={p.infoTooltip} getIconDiv={() => <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>} />}
          </>
        )
      }
    ];
  } else {
    columns = [
      { display: 'Date', isLeftAligned: true, size: 1 },
      { display: 'Product', isLeftAligned: true, size: 3, mobileSize: 2 },
      { display: 'Merchant', isDeskTopOnly: true, size: 1 },
      { display: 'Order Total', isDeskTopOnly: true, size: 1 },
      { display: 'Commission', size: 1 },
      { display: 'Code', isDeskTopOnly: true, size: 1 },
      { display: 'Status', size: 1 }
    ];
  }

  const largeScreen = window.innerWidth > DEVICE_WIDTH_BREAKPOINTS.MD;
  const gridTemplateColumns = largeScreen
    ? columns.map(({ size }) => `${size}fr`).join(' ')
    : columns
        .filter(column => !column.isDeskTopOnly)
        .map(({ size, mobileSize }) => `${mobileSize || size}fr`)
        .join(' ');

  const payouts = useMemo(() => {
    const positiveEarningItems = _.filter(items, item => item && item.commission_amount);
    const orderedItems = _.orderBy(positiveEarningItems, item => new Date(item.transaction_date), 'desc');
    return orderedItems.map(item => {
      const { statusDisplay, isLocked, isPaid, isInReview, source } = item;
      const isBonus = source === 'bonus';
      return {
        ...item,
        statusDisplay: statusDisplay,
        infoTooltip: isPaid
          ? null
          : isLocked
          ? 'This payout is locked and will be paid to you at the end of the week.'
          : isInReview
          ? `This payout is currently under review to ensure it is valid. We have a review for large orders to ensure merchants are correctly reporting commissions.`
          : isBonus
          ? 'We’re still waiting on this payment from the brand. We’ll pay you on the first Friday after we receive payment from the brand.'
          : `Pending means this is what you are on track to earn, but the order hasn't passed the retailer's return window (this can be up to 105 days). If the order is not cancelled or returned, you should receive this commission.`
      };
    });
  }, [user]);
  const hasMorePayouts = payouts.length > payoutsVisible;

  const downloadCommissions = async () => {
    if (isDownloading) return;
    setIsDownloading(true);

    const offsetFromUTC = new Date().getTimezoneOffset();
    try {
      const resp = await downloadCommissionsAPI({
        type,
        User_id: getUserId(props.user),
        timezoneOffset: offsetFromUTC
      });
      downloadCsvFromUrl(resp.url);
      window.ALERT.success('Successfully Downloaded!');
    } catch (error) {
      window.ALERT.error(error || `Error downloading, please try again.`);
    }

    setIsDownloading(false);
  };

  return (
    <div className='earnings-portal-commissions-outer'>
      <div className='earnings-portal-commissions-inner'>
        <div className='earnings-portal-section-header'>
          <div>
            <div className='earnings-portal-section-title'>{title}</div>
            {subtitle && <div className='earnings-portal-section-subtitle'>{subtitle}</div>}
          </div>
          <div>
            <button className='earnings-portal-section-button' onClick={downloadCommissions}>
              Download
            </button>
          </div>
        </div>

        <div className='earnings-portal-table'>
          <div className='earnings-portal-table-header' style={{ gridTemplateColumns }}>
            {columns.map(column => (
              <div
                className={cn('earnings-portal-table-cell', {
                  left: column.isLeftAligned,
                  'desktop-only': column.isDeskTopOnly
                })}
              >
                {column.display}
              </div>
            ))}
          </div>

          <div className='earnings-portal-table-body'>
            {payoutsAreLoading ? (
              new Array(5).fill(0).map((_, index) => {
                return (
                  <div key={index} className='earnings-portal-table-row' style={{ gridTemplateColumns }}>
                    {columns.map(column => (
                      <div className='skeleton-cell earnings-portal-table-cell'></div>
                    ))}
                  </div>
                );
              })
            ) : payouts.length ? (
              payouts.slice(0, payoutsVisible).map((payout, idx) => {
                const {
                  Collection_id,
                  title,
                  Product_title,
                  Product_id,
                  merchant,
                  code,
                  statusDisplay,
                  amountEarned,
                  infoTooltip,
                  transaction_date,
                  order_amount,
                  locked_user_percentage,
                  image
                } = payout;
                const bonusTooltip = locked_user_percentage && locked_user_percentage > 100 ? `Includes a Bonus` : null;

                return (
                  <div key={idx} className='earnings-portal-table-row' style={{ gridTemplateColumns }}>
                    {type === 'opportunity_commissions' ? (
                      <>
                        {columns.map(column => {
                          return (
                            <div
                              className={cn('earnings-portal-table-cell', {
                                left: column.isLeftAligned,
                                'desktop-only': column.isDeskTopOnly
                              })}
                            >
                              {column.getDisplay(payout)}
                            </div>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        <div className='left earnings-portal-table-cell'>{getPrettyDate(transaction_date)}</div>
                        {/* {title ? ( */}
                        <div className='image-container left payout-title'>
                          <Image src={image} className='commission-image desktop-only' failedText='S' useGenericFailedStyles />
                          <a
                            href={Collection_id ? `/collections/${Collection_id}` : Product_id ? `/shop/product/${Product_id}` : '/links'}
                            target='_blank'
                            rel='noopener nofollow noreferrer'
                            className='left'
                          >
                            {title || Product_title}
                          </a>
                        </div>
                        {/* ) : ( */}
                        {/* <div className='left earnings-portal-table-cell'>—</div> */}
                        {/* )} */}
                        <div className='desktop-only earnings-portal-table-cell'>{merchant}</div>
                        <div className='desktop-only earnings-portal-table-cell'>${order_amount && order_amount.toFixed(2)}</div>
                        <div className='earnings-portal-table-cell'>
                          ${amountEarned && commaNumber(amountEarned.toFixed(2))}
                          {bonusTooltip && (
                            <Tooltip message={bonusTooltip} getIconDiv={() => <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>} />
                          )}
                        </div>
                        <div className='desktop-only earnings-portal-table-cell'>{code || '—'}</div>
                        <div className='earnings-portal-table-cell'>
                          {statusDisplay}
                          {infoTooltip && (
                            <Tooltip message={infoTooltip} getIconDiv={() => <FontAwesomeIcon icon={faInfoCircle}></FontAwesomeIcon>} />
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            ) : (
              <div className='earnings-portal-empty-message'>{emptyMessage}</div>
            )}

            {payouts.length > 5 && !payoutsAreLoading && (
              <div className='earnings-portal-table-row' style={{ gridTemplateColumns: '1fr' }}>
                <div className='show-more' onClick={() => (hasMorePayouts ? setPayoutsVisible(payoutsVisible + 15) : setPayoutsVisible(15))}>
                  {hasMorePayouts ? 'Show More' : 'Show Less'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EarningsPortalCommissions.propTypes = {
  user: PropTypes.object.isRequired,
  payoutsAreLoading: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(['all_commissions', 'normal_commissions', 'opportunity_commissions']).isRequired
};

export default EarningsPortalCommissions;
